<template>
  <b-container id="app-container" fluid>
    <b-row v-if="!isBusy" no-gutters>
      <b-col md="6" class="pl-2 pr-2">
         <b-row>
          <b-col md="7" class="my-1">
            <b-button-group size="md">
              <b-button
                :pressed.sync="inprocessActive"
                :disabled="grantedActive || smpActive"
                variant="outline-success"
                v-b-tooltip.hover.bottomleft
                :title="$t('dashboard.toggle-inprocess')"
                @click="updateStatus()"
              >
                {{ $t('dashboard.in-process') }}
              </b-button>
              <b-button
                :pressed.sync="grantedActive"
                variant="outline-success"
                :disabled="inprocessActive"
                v-b-tooltip.hover.bottomleft
                :title="$t('dashboard.toggle-granted')"
                @click="updateStatus()"
              >
                {{ $t('dashboard.granted') }}
              </b-button>
              <b-button
                v-if="grantedActive"
                :pressed.sync="smpActive"
                variant="outline-success"
                :disabled="inprocessActive || !grantedActive"
                v-b-tooltip.hover.bottomleft
                :title="$t('dashboard.toggle-smp')"
                @click="updateStatus()"
              >
                {{ $t('dashboard.smp') }}
              </b-button>
            </b-button-group>
          </b-col>
        </b-row>
        <b-row v-if="isFiltered || isDashboardFiltered">
          <b-col md="8">
            <b-input-group>
              <b-form-input
                v-model="filterString"
                :placeholder="$t('dashboard.placeholder')"
                @input="updateFilter()">
              </b-form-input>
            </b-input-group>
          </b-col>
          <b-col md="4">
            <b-button
              variant="danger"
              :disabled="!isFiltered"
              @click="clearFilters()">
              {{ $t('dashboard.reset') }}
            </b-button>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="12" class="p-0">
            <app-stats :applications="filteredItems"></app-stats>
          </b-col>
        </b-row>
        <div v-show="!isDashboardFiltered">
          <b-row>
            <b-col md="12" class="mt-2 p-0">
                <bar-chart
                  id="approvedChart"
                  v-if="approvedByYearStats"
                  :stats="approvedByYearStats">
                  <template v-slot:title>{{ $t('dashboard.approved-by-year') }}</template>
                </bar-chart>
              </b-col>
          </b-row>
        </div>
        <div v-show="isDashboardFiltered">
          <b-row class="mt-4">
            <b-col md="6" class="my-1">
              <chart-filter
                id="orgChart"
                v-if="orgStats && filteredItems.length"
                @filter="org = $event; updateFilter();"
                :stats="orgStats"
                :selectedItem="org"
                :status=status
                :num="filteredItems.length">
                <template v-slot:title>
                  {{ orgChartTitle }}
                </template>
                <template v-slot:instructions>{{ $t('dashboard.org-filter-instruction') }}</template>
                <template v-slot:category>{{ $t('dashboard.organisations') }}</template>
              </chart-filter>
            </b-col>
            <b-col md="6" class="my-1">
              <chart-filter
                id="provinceChart"
                v-if="provinceStats && filteredItems.length"
                @filter="province = $event; updateFilter();"
                :stats="provinceStats"
                :selectedItem="province"
                :status=status
                :num="filteredItems.length">
                <template v-slot:title>{{ provChartTitle }}</template>
                <template v-slot:instructions>{{ $t('dashboard.prov-filter-instruction') }}</template>
                <template v-slot:category>{{ $t('dashboard.provinces') }}</template>
              </chart-filter>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="12" class="my-1">
              <b-table
                v-show="filteredItems.length"
                id="applications"
                class="apptable pr-1"
                primary-key="id"
                borderless
                responsive
                hover
                small
                selectable
                select-mode="single"
                selectedVariant="light"
                sticky-header="250px"
                show-empty
                :items="applications"
                :fields="fields"
                :busy="isBusy"
                :per-page="perPage"
                :current-page="currentPage"
                :filter="filter"
                :filter-function="filterItems"
                @filtered="onFiltered"
                ref="table"
                @row-hovered="hover($event)"
                @row-unhovered="unhover($event)"
                @row-clicked="rowClicked($event)">
                <div slot="table-busy" class="text-center text-dark my-2">
                  <b-spinner class="align-middle"></b-spinner>
                </div>
                <template #cell(name)="data">
                  <div class="d-flex justify-content-start text-truncate">
                    <span>{{ data.value }}</span>
                    <span
                      class="pl-2 text-info"
                      v-if="!data.item.locations.features.length">
                      <div :id="'info_' + data.item.id">
                        <i class="fas fa-info-circle"></i>
                      </div>
                      <b-popover
                        :target="'info_' + data.item.id"
                        variant="warning"
                        triggers="hover"
                        placement="topright">
                        <span class="text-dark">{{ $t('dashboard.no-geodata') }}</span>
                      </b-popover>
                    </span>
                  </div>
                </template>
                <template #cell(approval_date)="data">
                  <div class="d-flex justify-content-start">
                    {{ formatApprovalDate(data.value) }}
                  </div>
                </template>
                <template #cell(area)="data">
                  <div class="d-flex justify-content-start pr-2">
                    {{ formatArea(data.value) }}
                  </div>
                </template>
                <template #cell(supporting_org)="data">
                  <div class="d-flex justify-content-start text-truncate">
                    {{ formatSupportingOrg(data.value) }}
                  </div>
                </template>
                <template #cell(smp_approved)="data">
                  <div class="d-flex justify-content-start text-truncate">
                    {{ formatSMPApproved(data.value) }}
                  </div>
                </template>
                <template #cell(empty)="data">
                  <h4 class="text-center">{{ data.emptyText }}</h4>
                </template>
                <template #cell(emptyFiltered)="data">
                  <h4>{{ data.emptyFilteredText }}</h4>
                </template>
                <!-- Header formatting -->
                <template slot="HEAD[area]" slot-scope="data">
                  <div class="d-flex justify-content-end">{{ data.label }}</div>
                </template>
                <template slot="HEAD[approval_date]" slot-scope="data">
                  <div class="d-flex justify-content-end">{{ data.label }}</div>
                </template>
                <template slot="HEAD[supporting_org]" slot-scope="data">
                  <div class="d-flex justify-content-end">{{ data.label }}</div>
                </template>
                <template slot="HEAD[smp_approved]" slot-scope="data">
                  <div class="d-flex justify-content-end">{{ data.label }}</div>
                </template>
              </b-table>
              <div v-if='filteredItems.length' class="mt-2 mb-3 d-flex align-items-center justify-content-start">
                <export-csv :items="filteredItems"></export-csv>
                <export-pdf class="px-4" :items="filteredItems"></export-pdf>
                <!-- <b-button variant="success" href="/applications/create">{{ $t('dashboard.btn-new-app') }}</b-button> -->
              </div>
              <b-alert
                :show="filteredItems.length === 0"
                class="mt-2 d-flex justify-content-center">
                {{ $t('dashboard.no-results') }}
              </b-alert>
            </b-col>
          </b-row>
        </div>
      </b-col>
      <b-col md="6">
        <app-map
          :filteredItems="filteredItems"
          :filterUrl="filterUrl"
          :bboxFilter="bbox"
          :status="status"
          :smp-approved="smpApproved"
          @map-filtered="filterMap($event)"
          @map-reset="resetMapFilter($event)">
        </app-map>
      </b-col>
    </b-row>
    <div
      v-if="isBusy"
      class="d-flex flex-column align-items-center justify-content-center"
      style="height: calc(100vh - 150px);">
      <b-spinner
        variant="success"
        type="grow"
        style="width: 5rem; height: 5rem;">
        <span class="sr-only">Loading</span>
      </b-spinner>
    </div>
  </b-container>
</template>
<script>
import * as _ from "lodash";
import ChartFilter from "@/components/charts/ChartFilter";
import BarChart from "@/components/charts/BarChart";
import Map from "@/components/map/Map";
import Stats from "@/components/charts/Stats";
import ExportCSV from "@/components/applications/ExportCSV";
import ExportPDF from "@/components/applications/ExportPDF";
import { booleanWithin, getType, intersect, polygon, simplify, bbox, bboxPolygon } from "@turf/turf";
import moment from "moment";
import numeral from "numeral";
export default {
  components: {
    chartFilter: ChartFilter,
    barChart: BarChart,
    appMap: Map,
    appStats: Stats,
    ExportCsv: ExportCSV,
    ExportPdf: ExportPDF
  },
  data() {
    return {
      applications: [],
      isDashboardFiltered: false,
      filter: null,
      filterString: "",
      filteredItems: [],
      status: null,
      inprocessActive: false,
      grantedActive: false,
      smpActive: false,
      smpApproved: null,
      org: null,
      province: null,
      bbox: null,
      perPage: 10,
      currentPage: 1,
      totalRows: 1,
      fields: [
        {
          key: "name",
          label: this.$i18n.t('dashboard.table.name-of-community'),
          sortable: true
        },
        {
          key: "province",
          label: this.$i18n.t('dashboard.table.province'),
          sortable: true
        },
        {
          key: "area",
          label: this.$i18n.t('dashboard.table.size'),
          sortable: true
        },
        {
          key: "approval_date",
          label: this.$i18n.t('dashboard.table.approved'),
          sortable: true
        },
        {
          key: "supporting_org",
          label: this.$i18n.t('dashboard.table.supported-by'),
          sortable: true
        },
        {
          key: "smp_approved",
          label: this.$i18n.t('dashboard.table.smp'),
          sortable: true
        }
      ],
      applications: [],
      isBusy: false
    };
  },

  computed: {
    orgStats() {
      return _.countBy(this.filteredItems, 'supporting_org');
    },

    provinceStats() {
      return _.countBy(this.filteredItems, "province");
    },

    smpApprovedStats() {
      return _.countBy(this.filteredItems, "smp_approved");
    },

    approvedByYearStats() {
      const filtered = this.applications.filter(app => app.concession_status === '0')
      const stats = _.countBy(filtered, "year_of_approval")
      delete stats['null']
      return stats
    },

    orgChartTitle(){
      if(this.grantedActive) return this.$t('dashboard.cfcl-by-org')
      if(this.inprocessActive) return this.$t('dashboard.ifc-by-org')
      if(this.smpActive) return this.$t('dashboard.smp-by-org')
    },

    provChartTitle(){
      if(this.grantedActive) return this.$t('dashboard.cfcl-by-province')
      if(this.inprocessActive) return this.$t('dashboard.ifc-by-province')
      if(this.smpActive) return this.$t('dashboard.smp-by-province')
    },

    isFiltered() {
      return (
        this.filterString ||
        this.status ||
        this.smpApproved||
        this.org ||
        this.province ||
        this.bbox
      );
    },

    filterUrl() {
      if (this.isFiltered) {
        let bbox = JSON.stringify(this.bbox);
        return `filterString=${this.filterString}&status=${this.status}&smp=${this.smpApproved}&org=${this.org}&province=${this.province}&bbox=${bbox}`;
      }
      return "";
    }
  },

  methods: {
    formatApprovalDate(date) {
      return date === null || date === ""
        ? " -- "
        : moment(date).format("DD/MM/YYYY");
    },

    formatArea(area) {
      return area === null || area === ""
        ? " -- "
        : numeral(area).format("0,0.00");
    },

    formatSupportingOrg(org){
      return org.substring(0, 10)
    },

    formatSMPApproved(approved){
      if(approved === true){
        return this.$i18n.t('dashboard.table.smp-approved-yes')
      }
      return this.$i18n.t('dashboard.table.smp-approved-no')
    },

    async getApplications() {
      this.isBusy = true;
      let applications = await this.$store.dispatch(
        "applications/getApplications",
        {
          vm: this
        }
      );
      this.applications = applications;
      this.totalRows = applications.length;
      this.isBusy = false;
      if (window.location.search) {
        this.filterByUrl();
      } else {
        this.filteredItems = applications;
        this.perPage = this.filteredItems.length;
      }
    },

    async getProvinces() {
      let provinces = await this.$store.dispatch("applications/getProvinces", {
        vm: this
      });
      this.provinces = provinces;
    },

    setStatus(status) {
      this.status = this.status === status ? null : status;
      this.updateFilter();
    },

    updateStatus(){
      this.isDashboardFiltered = true;
      if(this.inprocessActive){
        this.status = '2'
        this.updateFilter();
        return;
      }
      if(this.grantedActive){
        this.status = '0';
        if(this.smpActive){
          this.smpApproved = true;
        }
        else {
          this.smpApproved = false;
        }
        this.updateFilter();
        return;
      }
      this.status = null;
      this.smpApproved = null;
      this.smpActive = false;
      this.updateFilter();
    },

    clearFilters() {
      this.isDashboardFiltered = true;
      this.filter = null;
      this.filterString = "";
      this.status = null;
      this.smpApproved = null;
      this.org = null;
      this.province = null;
      this.inprocessActive = false;
      this.grantedActive = false;
      this.smpActive = false;
      this.bbox = null;
      history.replaceState(null, '', '/applications/')

      this.$root.$emit("clear-filters");
    },

    filterItems(application, filter) {
      if (filter === null) {
        return true;
      }

      let textFound = JSON.stringify(application)
        .toLowerCase()
        .includes(filter.filterString.toLowerCase());
      let statusFound = true;
      let smpApproved = true;
      let orgFound = true;
      let provinceFound = true;
      let bboxIntersects = true;
      if (textFound) {
        if (filter.status) {
          statusFound = application.concession_status === filter.status;
        }
        if(filter.smpApproved){
          smpApproved = application.smp_approved === filter.smpApproved;
        }
        if (filter.org) {
          orgFound = application.supporting_org === filter.org;
        }
        if (filter.province) {
          provinceFound = application.province === filter.province;
        }
        if (filter.bbox) {
          // test if any of the applications locations intersect with the filter
          bboxIntersects = _.some(application.locations.features, feature => {
            let type = getType(feature.geometry);
            if (type === "Polygon") {
              try{
                let intersection = intersect(bboxPolygon(bbox(feature.geometry)), filter.bbox);
                return intersection !== null ? true : false;
              }
              catch(err){
                console.warn(err, feature.properties.application.name, type);
              }
            }
            if (type === "MultiPolygon") {
              let polys = feature.geometry.coordinates.map(c => polygon(c));
              // test for intersection with teh bounding box of the first polygon of MultiPolygon
              try{
                let intersection = intersect(bboxPolygon(bbox(polys[0])), filter.bbox);
                return intersection !== null ? true : false;
              }catch(err){
                console.warn(err, feature.properties.application.name, type);
              }
            }
            if (type === "Point" || type === "LineString") {
              try{
                return booleanWithin(feature.geometry, filter.bbox);
              }catch(err){
                console.warn(err, feature.properties.application.name);
              }
            }
            return false;
          });
        }
        return (
          textFound &&
          statusFound &&
          smpApproved &&
          orgFound &&
          provinceFound &&
          bboxIntersects
        );
      }
      return textFound;
    },

    onFiltered(filteredItems) {
      this.filteredItems = filteredItems;
      this.totalRows = filteredItems.length;
      this.perPage = filteredItems.length;
      this.currentPage = 1;
    },

    updateFilter() {
      if (
        this.filterString === "" &&
        !this.status &&
        !this.smpApproved &&
        !this.org &&
        !this.province &&
        !this.bbox
      ) {
        this.filter = null;
      }
      this.filter = {
        filterString: this.filterString,
        status: this.status,
        smpApproved: this.smpApproved,
        org: this.org,
        province: this.province,
        bbox: this.bbox
      };
    },

    filterByUrl() {
      this.isDashboardFiltered = true;
      let params = new URLSearchParams(window.location.search);
      this.filterString = params.get("filterString");
      let status = params.get("status");
      let smpApproved = params.get('smp');
      let org = params.get("org");
      let province = params.get("province");
      let bbox = params.get("bbox");
      this.status = status === "null" || status === "" ? null : status;
      this.smpApproved = smpApproved === "false" || smpApproved === "" ? null : true;
      if(this.status){
        this.status == '2' ? this.inprocessActive = true : this.grantedActive = true;
      }
      if(this.smpApproved){
        this.smpApproved ? this.smpActive = true : this.smpActive = false;
      }
      this.org = org === "null" || org === "" ? null : org;
      this.province = province === "null" || province === "" ? null : province;
      this.bbox =
        bbox === "null" || bbox === "" ? null : JSON.parse(params.get("bbox"));
      this.$nextTick(() => {
        this.updateFilter();
      });
    },

    hover(application) {
      this.$root.$emit("table-hovered", application);
    },

    unhover(application) {
      this.$root.$emit("table-unhovered", application);
    },

    rowClicked(app) {
      const url = "/applications/" + app.id + "?" + this.filterUrl;
      window.open(url)
      // location.href = url
    },

    filterMap(bbox) {
      this.bbox = bbox;
      this.updateFilter();
    },

    resetMapFilter() {
      this.bbox = null;
      this.updateFilter();
    },
  },

  async beforeMount() {
    await this.getApplications();
    await this.getProvinces();
  },

  created() {
    let _vm = this;
    this.$i18n.locale = lang;
    _vm.$root.$on("feature-hovered", app => {
      let table = _vm.$refs.table;
      if (table) {
        let row = _.find(table.$refs.itemRows, tr => {
          return tr.$attrs.id === "applications__row_" + app.id;
        });
        if (row) {
          row.$el.className = "hover";
        }
      }
    });
    _vm.$root.$on("feature-unhovered", app => {
      let table = _vm.$refs.table;
      if (table) {
        let row = _.find(table.$refs.itemRows, tr => {
          return tr.$attrs.id === "applications__row_" + app.id;
        });
        if (row) {
          row.$el.className = "";
        }
      }
    });
  }
};
</script>
<style scoped>
#app-container {
  padding: 0px;
  min-height: calc(100vh - 130px);
}

.apptable {
  margin-top: 15px;
  max-height: 250px;
}
#scroll-table {
  overflow: hidden;
  max-height: 50%;
}
.ps {
  height: 800px;
}
</style>
